import { forwardRef, useEffect, useRef, useState } from "react";
import "./FileManagerLeftSideTree.css";
import SlickGrid from "@CubeComponents/html/SlickGrid";
import { Formatters } from "slickgrid-react";
import _ from "lodash";
import folderImg from "../../assets/icons/svg/folder.svg";
import folderLockImg from "../../assets/icons/svg/folderlock.svg";
import spaceImg from "../../assets/icons/svg/spaces.svg";

interface LeftSideTreeProps {
  setSelectedFolder: Function;
  selectedFolder: any;
  fileManagerData: any;
  onFileLoad: Function;
}
const LeftSideTree = forwardRef(
  ({ setSelectedFolder, selectedFolder, fileManagerData, onFileLoad }: LeftSideTreeProps, ref) => {
    const [folderTreeData, setFolderTreeData] = useState<any>([]);
    const tempRef = useRef<any>(null);
    const treeViewRef: any = ref || tempRef;

    function sortWithHierarchy(arr: any) {
      const byParent = arr.reduce((acc: any, item: any) => {
        if (!acc[item.parentId]) {
          acc[item.parentId] = [];
        }
        acc[item.parentId].push(item);
        return acc;
      }, {});

      function sortRecursive(parentId: any) {
        const children = byParent[parentId] || [];
        children.sort((a: any, b: any) => a.name.localeCompare(b.name));

        return children.flatMap((child: any) => [child, ...sortRecursive(child.id)]);
      }

      return sortRecursive("0");
    }

    useEffect(() => {
      const allItems =
        _.cloneDeep(fileManagerData.files)?.filter(
          (item: any) =>
            ["RESOURCE_TYPE_TRANSMITTAL", "RESOURCE_TYPE_FOLDER"].includes(item.resourceType) && item.id != "0",
        ) || [];

      onFileLoad(allItems);

      const root: any = {
        ...fileManagerData.cwd,
        nodeId: fileManagerData.cwd?.id || "cwd",
        id: "0",
        indentLevel: 0,
        parentId: null,
        treeLevel: 0,
        hasChild: true,
        __hasChildren: true,
        __collapsed: false,
        isExpanded: true,
      };

      let updatedData: any = sortWithHierarchy(allItems);
      updatedData = updatedData.map((d: any) => {
        const level = d.path?.length ?? 0;
        return {
          ...d,
          indentLevel: level === 1 ? 2 : level + 1,
          hasChild: !!d.hasChild,
          __hasChildren: !!d.hasChild,
          treeLevel: level + 1,
          __collapsed: !allItems.find((item: any) => item.parentId == d.id),
          isExpanded: !!allItems.find((item: any) => item.parentId == d.id),
        };
      });
      setFolderTreeData([root, ...updatedData]);
    }, [fileManagerData.files]);

    useEffect(() => {
      if (treeViewRef.current) {
        setTimeout(() => {
          treeViewRef.current?.slickGrid?.setSelectedRows(["0"]);
        }, 200);
      }
    }, [treeViewRef.current]);

    useEffect(() => {
      if (treeViewRef.current?.dataView) {
        const dataView = treeViewRef.current?.dataView;
        const rowIndex = dataView.getRowById(selectedFolder.id);
        treeViewRef.current.slickGrid.setSelectedRows([rowIndex]);

        if (selectedFolder.isBulkFetch) {
          const index = folderTreeData.findIndex((d: any) => d.id == selectedFolder.id);
          const node = treeViewRef.current.slickGrid.getCellNode(index, 0); // td element
          const rowNode = node?.closest(".slick-row");
          if (rowNode) {
            rowNode.scrollIntoView({ behavior: "auto", block: "center" });
          }
        }
      }
    }, [selectedFolder.id, folderTreeData]);

    const handleOnClick = (args: any) => {
      const rowIndex = args.detail.args.row;
      const clickedRow = treeViewRef.current.slickGrid.getDataItem(rowIndex);
      setSelectedFolder(clickedRow);
    };

    const titleFormatter = (data: any, value: any, a: any, b: any, args: any) => {
      let iconSrc = folderImg;
      if (!args?.permission?.read) {
        iconSrc = folderLockImg;
      }
      if (args.id == "0") {
        iconSrc = spaceImg;
      }
      return `<span style="display:flex;align-items:center;gap:6px; ">
      <img src="${iconSrc}" alt="icon" style="width:20px;height:20px;" />
      ${args.name}
    </span>
  `;
    };

    const collapseParentNodesWithoutChildData = () => {
      setTimeout(() => {
        treeViewRef.current?.treeDataService.toggleTreeDataCollapse(false);
      }, 0);
    };

    const getTreeView = () => (
      <SlickGrid
        ref={treeViewRef}
        gridOptions={{
          forceFitColumns: true,
          enableCheckboxSelector: false,
          enableContextMenu: false,
        }}
        titleFormatter={titleFormatter}
        selectCheckBoxOnRowClick={true}
        disableTreeCollapseOnDataChange={true}
        enableMultiSelect={false}
        surpassedDataset={folderTreeData}
        // dataset={folderTreeData}
        enableFiltering
        enablePointerForRows
        onClick={handleOnClick}
        showPageCount={false}
        disableRowBorder={true}
        hasChildMapping="hasChild"
        parentIdMapping="parentId"
        treeGrid={true}
        setGridReady={collapseParentNodesWithoutChildData}
        disableChildDataFetch
        initialSortColumnId="name"
        columnDefinitions={[
          {
            id: "name",
            name: "Name",
            field: "name",
            sortable: true,
            resizable: true,
            formatter: Formatters.tree,
            minWidth: 300,
          },
        ]}
        height="100%"
        disableHeader
      />
    );
    return <div className="w-full overflow-auto flex h-full">{getTreeView()}</div>;
  },
);

LeftSideTree.displayName = "LeftSideTree";
export default LeftSideTree;
